import { Link } from 'react-router-dom';
import {
  Container,
  Title,
  Table,
  Badge,
  Card,
  Text,
  Stack,
  Group,
  Anchor,
  Loader,
  Alert,
  Pagination,
  Paper,
  SimpleGrid,
  TextInput,
  CopyButton,
  Button,
} from '@mantine/core';
import { IconExclamationCircle, IconGift, IconCopy, IconCheck } from '@tabler/icons-react';
import { useState } from 'react';
import { useAuth } from '@clerk/react';
import { useApi, useSession } from '../../api';
import ProtectedRoute from '../../components/auth/ProtectedRoute';
import { formatCents } from '../../components/products/productDisplay';

const PAGE_LIMIT = 20;

const formatDate = (dateString) => {
  if (!dateString) return '—';
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const StatTile = ({ label, value }) => (
  <Paper withBorder p="md" radius="md">
    <Stack gap={2}>
      <Text size="xs" c="dimmed">{label}</Text>
      <Text size="xl" fw={700}>{value}</Text>
    </Stack>
  </Paper>
);

// One ReferralCommission row (contract §4.22) — 5% of a referred seller's
// completed income. Same source_type vocabulary as EarningRecord (§3.16);
// rows with `reversed_at` were clawed back with their source (§4.20) and stay
// listed, flagged and de-emphasized like the earnings ledger.
const CommissionRow = ({ record }) => {
  const reversed = !!record.reversed_at;
  return (
    <Table.Tr>
      <Table.Td>{formatDate(record.created_at)}</Table.Td>
      <Table.Td>
        {record.source_type === 'tip' ? (
          <Badge color="pink" variant="light">Tip</Badge>
        ) : record.source_type === 'fan_sub' ? (
          <Badge color="teal" variant="light">Fan sub</Badge>
        ) : (
          <Badge color="grape" variant="light">Album sale</Badge>
        )}
      </Table.Td>
      <Table.Td>
        <Group gap="xs" wrap="nowrap">
          {record.seller ? (
            <Anchor component={Link} to={`/princess/${record.seller.id}`} size="sm">
              {record.seller.display_name}
            </Anchor>
          ) : (
            <Text size="sm" c="dimmed">—</Text>
          )}
          {reversed && (
            <Badge color="red" variant="light">Reversed</Badge>
          )}
        </Group>
      </Table.Td>
      <Table.Td>
        <Text
          size="sm"
          fw={600}
          c={reversed ? 'dimmed' : undefined}
          td={reversed ? 'line-through' : undefined}
        >
          {formatCents(record.amount_cents)}
        </Text>
      </Table.Td>
    </Table.Tr>
  );
};

// Referral program (contract §4.22) — GET /api/v1/me/referrals.
// Shows the shareable link (the first fetch lazily generates the code),
// summary numbers and the paginated commission ledger.
const ReferralsPage = () => {
  const [page, setPage] = useState(1);
  const { isSignedIn } = useAuth();
  const { isSeller } = useSession();

  const { data, loading, error } = useApi(
    '/api/v1/me/referrals',
    { page, limit: PAGE_LIMIT },
    { enabled: !!isSignedIn, query: { placeholderData: (prev) => prev } }
  );
  const commissions = data?.items ?? [];
  const totalPages = data ? Math.max(1, Math.ceil(data.total / data.limit)) : 1;
  const referralLink = data?.referral_code
    ? `${window.location.origin}/?ref=${data.referral_code}`
    : '';

  return (
    <ProtectedRoute requireAuth={true}>
      <Container size="lg" py="xl">
        <Stack gap="xl">
          <Group gap="xs" align="center">
            <IconGift size="1.5rem" color="var(--mantine-color-pink-6)" />
            <Title order={1}>Referrals</Title>
          </Group>

          <Stack gap={4}>
            <Text>
              Earn 5% of what sellers you refer make, for their first 12 months.
            </Text>
            {!isSeller && (
              <Text size="sm" c="dimmed">
                Accrued commissions become withdrawable through the seller
                payout flow once you have an approved seller profile.
              </Text>
            )}
          </Stack>

          {error && (
            <Alert icon={<IconExclamationCircle size="1rem" />} color="red">
              {error.message}
            </Alert>
          )}

          {loading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
              <Loader size="lg" />
            </div>
          ) : data && (
            <>
              <Card shadow="sm" padding="lg" radius="md" withBorder>
                <Stack gap="md">
                  <Text fw={600}>Your referral link</Text>
                  <Group align="flex-end" gap="sm" wrap="nowrap">
                    <TextInput
                      value={referralLink}
                      readOnly
                      aria-label="Referral link"
                      style={{ flex: 1, maxWidth: 480 }}
                    />
                    <CopyButton value={referralLink} timeout={1500}>
                      {({ copied, copy }) => (
                        <Button
                          variant="light"
                          color={copied ? 'teal' : 'pink'}
                          onClick={copy}
                          leftSection={copied ? <IconCheck size="1rem" /> : <IconCopy size="1rem" />}
                        >
                          {copied ? 'Copied' : 'Copy link'}
                        </Button>
                      )}
                    </CopyButton>
                  </Group>
                  <SimpleGrid cols={{ base: 1, xs: 2 }} spacing="sm">
                    <StatTile label="Referred users" value={data.referred_count ?? 0} />
                    <StatTile
                      label="Commissions earned"
                      value={formatCents(data.commission_total_cents ?? 0)}
                    />
                  </SimpleGrid>
                </Stack>
              </Card>

              {commissions.length === 0 ? (
                <Card shadow="sm" padding="lg" radius="md" withBorder>
                  <Stack align="center" gap="md" py="xl">
                    <IconGift size={48} color="var(--mantine-color-gray-4)" />
                    <Text c="dimmed" size="lg">No commissions yet</Text>
                    <Text c="dimmed" size="sm">
                      Share your link — when someone you refer becomes a
                      selling princess, their earnings show up here.
                    </Text>
                  </Stack>
                </Card>
              ) : (
                <Card shadow="sm" padding={0} radius="md" withBorder>
                  <Table.ScrollContainer minWidth={560}>
                    <Table striped highlightOnHover verticalSpacing="sm">
                      <Table.Thead>
                        <Table.Tr>
                          <Table.Th>Date</Table.Th>
                          <Table.Th>Type</Table.Th>
                          <Table.Th>Referred seller</Table.Th>
                          <Table.Th>Commission</Table.Th>
                        </Table.Tr>
                      </Table.Thead>
                      <Table.Tbody>
                        {commissions.map((record) => (
                          <CommissionRow key={record.id} record={record} />
                        ))}
                      </Table.Tbody>
                    </Table>
                  </Table.ScrollContainer>
                </Card>
              )}
            </>
          )}

          {totalPages > 1 && (
            <Group justify="center">
              <Pagination value={page} onChange={setPage} total={totalPages} />
            </Group>
          )}
        </Stack>
      </Container>
    </ProtectedRoute>
  );
};

export default ReferralsPage;
